import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router';
import { api, uitvoeren } from '../../api/client.ts';
import { useCvIntake } from '../../api/cvIntake.ts';
import { certificaattypeLijst, functietitelLijst, talentpoolLijst, vaardigheidLijst } from '../../api/referentie.ts';
import type {
  Certificaattype,
  CvAnalyse,
  Functietitel,
  Kandidaat,
  Kandidaatvoorstel,
  Locatie,
  Rijbewijs,
  Talentpool,
  Vaardigheid,
} from '../../api/typen.ts';
import { FormulierDialoog } from '../../componenten/FormulierDialoog.tsx';
import { FunctietitelsVeld, LocatieVeld, TalentpoolsVeld } from '../../componenten/keuzevelden.tsx';
import { Meerkeuzeveld, Tekstveld } from '../../componenten/velden.tsx';
import { datum } from '../../helpers/formatteer.ts';
import { leegNaarNull } from '../../helpers/formulier.ts';
import {
  label,
  opleidingsniveauLabels,
  opties,
  rijbewijsLabels,
  taalniveauLabels,
  taalnaam,
} from '../../helpers/labels.ts';
import { useVerzenden } from '../../hooks/useVerzenden.ts';

const toegestaan = '.pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const rijbewijsOpties = opties(rijbewijsLabels);

interface Waarden {
  kandidaat: {
    voornaam: string;
    achternaam: string;
    geboortedatum: string;
    email: string;
    telefoon: string;
    linkedinUrl: string;
    straatHuisnummer: string;
    locatie: Locatie | null;
    rijbewijzen: Rijbewijs[];
    functietitels: Functietitel[];
    talentpools: Talentpool[];
  };
}

export interface Referenties {
  talentpools: Map<string, Talentpool>;
  functietitels: Map<string, Functietitel>;
  vaardigheden: Map<string, Vaardigheid>;
  certificaattypes: Map<string, Certificaattype>;
  locatie: Locatie | null;
}

const perId = <T extends { id: string }>(lijst: T[]) => new Map(lijst.map((item) => [item.id, item]));

/**
 * Laadt de referentielijsten om ids uit een voorstel als naam te tonen, en optioneel een herkende locatie.
 * @param locatieId De herkende locatie, of null.
 * @returns De lijsten per id.
 */
export async function laadReferenties(locatieId: string | null): Promise<Referenties> {
  const [talentpools, functietitels, vaardigheden, certificaattypes, locatie] = await Promise.all([
    talentpoolLijst.zoek(''),
    functietitelLijst.zoek(''),
    vaardigheidLijst.zoek(''),
    certificaattypeLijst.zoek(''),
    locatieId ? uitvoeren(api.GET('/api/v1/locaties/{id}', { params: { path: { id: locatieId } } })) : null,
  ]);
  return {
    talentpools: perId(talentpools),
    functietitels: perId(functietitels),
    vaardigheden: perId(vaardigheden),
    certificaattypes: perId(certificaattypes),
    locatie,
  };
}

function beginwaarden(voorstel: Kandidaatvoorstel | null, referenties: Referenties | null): Waarden {
  const k = voorstel?.kandidaat;
  const zoek = <T,>(ids: string[] | undefined, kaart: Map<string, T> | undefined) =>
    (ids ?? []).map((id) => kaart?.get(id)).filter((item): item is T => item !== undefined);
  return {
    kandidaat: {
      voornaam: k?.voornaam ?? '',
      achternaam: k?.achternaam ?? '',
      geboortedatum: k?.geboortedatum ?? '',
      email: k?.email ?? '',
      telefoon: k?.telefoon ?? '',
      linkedinUrl: k?.linkedinUrl ?? '',
      straatHuisnummer: k?.straatHuisnummer ?? '',
      locatie: referenties?.locatie ?? null,
      rijbewijzen: k?.rijbewijzen ?? [],
      functietitels: zoek(k?.functietitelIds, referenties?.functietitels),
      talentpools: zoek(k?.talentpoolIds, referenties?.talentpools),
    },
  };
}

export const adviesteksten: Record<CvAnalyse['talentpoolAdvies']['status'], string> = {
  toegewezen: 'Jev heeft een talentpool gekozen met voldoende zekerheid.',
  onzeker: 'Jev twijfelt: geen enkele talentpool is zeker genoeg. Kies zelf.',
  verdeeld: 'Jev twijfelt tussen meerdere talentpools. Kies zelf.',
  geen_passende: 'Volgens Jev past geen van de talentpools bij dit profiel.',
  geen_talentpools: 'Er zijn nog geen talentpools om uit te kiezen.',
  niet_ingesteld: 'Het beslissingsmodel Jev is niet ingesteld.',
  mislukt: 'Het talentpooladvies van Jev is mislukt. Kies zelf.',
};

interface Props {
  open: boolean;
  onSluit: () => void;
  onAangemaakt?: ((kandidaat: Kandidaat) => void) | undefined;
}

/**
 * Maakt een kandidaat aan uit een cv. Eerst leest AI het cv uit (een Word-bestand wordt daarvoor naar pdf
 * omgezet) en adviseert Jev een talentpool; de recruiter controleert het voorstel en slaat het daarna op.
 * Pas bij opslaan worden de kandidaat, het cv en de kwalificaties vastgelegd.
 * @param props Of de dialoog open is, en wat er gebeurt na sluiten en na aanmaken.
 * @returns De dialoog.
 */
export function KandidaatUitCvDialoog({ open, onSluit, onAangemaakt }: Props) {
  const intake = useCvIntake();
  const [bestand, setBestand] = useState<File | null>(null);
  const [analyse, setAnalyse] = useState<CvAnalyse | null>(null);
  const [referenties, setReferenties] = useState<Referenties | null>(null);
  const [voorstel, setVoorstel] = useState<Kandidaatvoorstel | null>(null);
  const [analysefout, setAnalysefout] = useState<string | null>(null);
  const formulier = useForm<Waarden>({ defaultValues: beginwaarden(null, null) });

  useEffect(() => {
    if (open) {
      setBestand(null);
      setAnalyse(null);
      setVoorstel(null);
      setAnalysefout(null);
      formulier.reset(beginwaarden(null, null));
    }
  }, [open, formulier]);

  const analyseer = async (gekozen: File) => {
    setBestand(gekozen);
    setAnalysefout(null);
    try {
      const resultaat = await intake.analyseer.mutateAsync(gekozen);
      const lijsten = await laadReferenties(resultaat.voorstel.kandidaat.locatieId);
      setAnalyse(resultaat);
      setReferenties(lijsten);
      setVoorstel(resultaat.voorstel);
      formulier.reset(beginwaarden(resultaat.voorstel, lijsten));
    } catch (fout) {
      setAnalysefout(fout instanceof Error ? fout.message : 'Het cv kon niet worden uitgelezen.');
    }
  };

  const { onVerzend, fout, bezig } = useVerzenden(
    formulier,
    async (waarden) => {
      if (!bestand || !voorstel) {
        return;
      }
      const k = waarden.kandidaat;
      const gegevens: Kandidaatvoorstel = {
        ...voorstel,
        kandidaat: {
          ...voorstel.kandidaat,
          voornaam: k.voornaam,
          achternaam: k.achternaam,
          geboortedatum: leegNaarNull(k.geboortedatum),
          email: leegNaarNull(k.email),
          telefoon: leegNaarNull(k.telefoon),
          linkedinUrl: leegNaarNull(k.linkedinUrl),
          straatHuisnummer: leegNaarNull(k.straatHuisnummer),
          locatieId: k.locatie?.id ?? null,
          rijbewijzen: k.rijbewijzen,
          functietitelIds: k.functietitels.map((item) => item.id),
          talentpoolIds: k.talentpools.map((item) => item.id),
        },
        locatie: k.locatie ? null : voorstel.locatie,
      };
      const { kandidaat } = await intake.maakKandidaat.mutateAsync({ bestand, gegevens });
      onAangemaakt?.(kandidaat);
    },
    { melding: 'Kandidaat aangemaakt uit cv', naSucces: onSluit },
  );

  const verwijder = <K extends 'werkervaringen' | 'opleidingen' | 'certificaten' | 'vaardigheden' | 'talen'>(
    soort: K,
    index: number,
  ) => setVoorstel((huidig) => (huidig ? { ...huidig, [soort]: huidig[soort].filter((_, i) => i !== index) } : huidig));

  const kiesTalentpool = (talentpool: Talentpool) => {
    const huidig = formulier.getValues('kandidaat.talentpools');
    if (!huidig.some((item) => item.id === talentpool.id)) {
      formulier.setValue('kandidaat.talentpools', [...huidig, talentpool], { shouldDirty: true });
    }
  };

  const gekozenLocatie = formulier.watch('kandidaat.locatie');
  const advies = analyse?.talentpoolAdvies;

  return (
    <FormProvider {...formulier}>
      <FormulierDialoog
        open={open}
        titel="Kandidaat uit cv"
        onSluit={onSluit}
        onVerzend={voorstel ? onVerzend : () => undefined}
        fout={fout}
        bezig={bezig || intake.analyseer.isPending}
        verzendLabel="Kandidaat aanmaken"
        maxWidth="md"
      >
        <Stack spacing={2}>
          <Box>
            <Button component="label" variant={voorstel ? 'outlined' : 'contained'} startIcon={<UploadFileIcon />}>
              {bestand ? 'Ander cv kiezen' : 'Cv kiezen (pdf of Word)'}
              <input
                hidden
                type="file"
                accept={toegestaan}
                onChange={(gebeurtenis) => {
                  const gekozen = gebeurtenis.target.files?.[0];
                  gebeurtenis.target.value = '';
                  if (gekozen) {
                    void analyseer(gekozen);
                  }
                }}
              />
            </Button>
            {bestand && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {bestand.name}
                {analyse?.omgezetNaarPdf && ' — omgezet naar pdf, wordt als pdf opgeslagen'}
              </Typography>
            )}
          </Box>

          {intake.analyseer.isPending && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                AI leest het cv uit en Jev kiest een talentpool…
              </Typography>
              <LinearProgress />
            </Box>
          )}
          {analysefout && <Alert severity="error">{analysefout}</Alert>}
          {!bestand && !intake.analyseer.isPending && (
            <Typography variant="body2" color="text.secondary">
              Kies een cv. AI vult de gegevens in en stelt een talentpool voor. Je controleert alles voordat er iets
              wordt opgeslagen.
            </Typography>
          )}

          {voorstel && analyse && (
            <>
              {analyse.bestaandeKandidaatId && (
                <Alert severity="warning">
                  Er bestaat al een kandidaat met dit e-mailadres.{' '}
                  <Link component={RouterLink} to={`/kandidaten/${analyse.bestaandeKandidaatId}`}>
                    Bekijk de kandidaat
                  </Link>
                  .
                </Alert>
              )}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Tekstveld naam="kandidaat.voornaam" label="Voornaam" verplicht />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Tekstveld naam="kandidaat.achternaam" label="Achternaam" verplicht />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Tekstveld naam="kandidaat.email" label="E-mailadres" type="email" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Tekstveld naam="kandidaat.telefoon" label="Telefoon" type="tel" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Tekstveld naam="kandidaat.geboortedatum" label="Geboortedatum" type="date" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Tekstveld naam="kandidaat.linkedinUrl" label="LinkedIn-profiel" type="url" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Tekstveld naam="kandidaat.straatHuisnummer" label="Straat en huisnummer" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <LocatieVeld
                    naam="kandidaat.locatie"
                    label="Woonplaats"
                    hulptekst={
                      gekozenLocatie
                        ? undefined
                        : voorstel.locatie
                          ? `Uit het cv: ${[voorstel.locatie.postcode, voorstel.locatie.plaats].filter(Boolean).join(' ')} (wordt aangemaakt)`
                          : undefined
                    }
                  />
                </Grid>
                <Grid size={12}>
                  <Meerkeuzeveld naam="kandidaat.rijbewijzen" label="Rijbewijzen" opties={rijbewijsOpties} />
                </Grid>
              </Grid>

              <Divider />
              <Stack spacing={1}>
                <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AutoAwesomeIcon fontSize="small" color="primary" /> Indeling
                </Typography>
                {advies && (
                  <Alert severity={advies.status === 'toegewezen' ? 'success' : 'info'}>
                    {adviesteksten[advies.status]}
                    {advies.kansen.length > 0 && (
                      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mt: 1 }}>
                        {advies.kansen.slice(0, 5).map((kans) => {
                          const pool = referenties?.talentpools.get(kans.talentpoolId);
                          return (
                            <Chip
                              key={kans.talentpoolId}
                              size="small"
                              label={`${kans.naam} ${Math.round(kans.kans * 100)}%`}
                              color={kans.talentpoolId === advies.talentpoolId ? 'primary' : 'default'}
                              onClick={pool ? () => kiesTalentpool(pool) : undefined}
                            />
                          );
                        })}
                      </Stack>
                    )}
                  </Alert>
                )}
                <FunctietitelsVeld naam="kandidaat.functietitels" label="Ingedeeld als functietitels" />
                <TalentpoolsVeld naam="kandidaat.talentpools" label="Talentpools" />
              </Stack>

              <Divider />
              <Typography variant="subtitle1">Uit het cv</Typography>
              <Kwalificatielijst
                titel="Werkervaring"
                regels={voorstel.werkervaringen.map((w) => ({
                  hoofd: w.functietitelLetterlijk,
                  sub: [
                    w.werkgever,
                    [datum(w.van), w.tot ? datum(w.tot) : 'heden'].filter(Boolean).join(' – '),
                    w.functietitelId
                      ? `herkend als ${referenties?.functietitels.get(w.functietitelId)?.naam ?? '…'}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · '),
                }))}
                onVerwijder={(index) => verwijder('werkervaringen', index)}
              />
              <Kwalificatielijst
                titel="Opleidingen"
                regels={voorstel.opleidingen.map((o) => ({
                  hoofd: o.richting ?? label(opleidingsniveauLabels, o.niveau),
                  sub: [o.niveau ? label(opleidingsniveauLabels, o.niveau) : null, o.instelling, o.eindjaar]
                    .filter(Boolean)
                    .join(' · '),
                }))}
                onVerwijder={(index) => verwijder('opleidingen', index)}
              />
              <Kwalificatielijst
                titel="Certificaten"
                regels={voorstel.certificaten.map((c) => ({
                  hoofd: referenties?.certificaattypes.get(c.certificaattypeId)?.naam ?? 'Certificaat',
                  sub: c.behaaldOp ? `behaald ${datum(c.behaaldOp)}` : '',
                }))}
                onVerwijder={(index) => verwijder('certificaten', index)}
              />
              <Kwalificatielijst
                titel="Vaardigheden"
                regels={voorstel.vaardigheden.map((v) => ({
                  hoofd: referenties?.vaardigheden.get(v.vaardigheidId)?.naam ?? 'Vaardigheid',
                  sub: '',
                }))}
                onVerwijder={(index) => verwijder('vaardigheden', index)}
              />
              <Kwalificatielijst
                titel="Talen"
                regels={voorstel.talen.map((t) => ({
                  hoofd: taalnaam(t.taalcode),
                  sub: label(taalniveauLabels, t.niveau),
                }))}
                onVerwijder={(index) => verwijder('talen', index)}
              />
              {(analyse.nietHerkend.vaardigheden.length > 0 || analyse.nietHerkend.certificaten.length > 0) && (
                <Alert severity="info">
                  Niet in de referentielijsten gevonden en dus niet overgenomen:{' '}
                  {[...analyse.nietHerkend.vaardigheden, ...analyse.nietHerkend.certificaten].join(', ')}.
                </Alert>
              )}
            </>
          )}
        </Stack>
      </FormulierDialoog>
    </FormProvider>
  );
}

function Kwalificatielijst({
  titel,
  regels,
  onVerwijder,
}: {
  titel: string;
  regels: { hoofd: string; sub: string }[];
  onVerwijder: (index: number) => void;
}) {
  if (regels.length === 0) {
    return null;
  }
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {titel}
      </Typography>
      <List dense disablePadding>
        {regels.map((regel, index) => (
          <ListItem
            key={`${regel.hoofd}-${index}`}
            disableGutters
            secondaryAction={
              <IconButton
                edge="end"
                size="small"
                aria-label={`${regel.hoofd} weglaten`}
                onClick={() => onVerwijder(index)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            <ListItemText primary={regel.hoofd} secondary={regel.sub || undefined} />
          </ListItem>
        ))}
      </List>
    </Box>
  );
}
